import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ProductTypeName } from '@electricjs/core_entity-client';
import { Box, Country, Flex, spaces, useBreakpoint } from '@electricjs/arc';

import { type Product } from '@/types/products';
import { Store } from '@/types/storefront';
import TranslatedPagination from 'common/TranslatedPagination';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import {
  ProductOriginName,
  useGetSuggestionsQuery,
} from '@/redux/slices/productApiSlice';

import { ErrorState } from '../ErrorState';
import { ItemCard } from '../ItemCard';
import { CollapseBox } from './CollapseBox';

import {
  ALL_COMPANY_PRODUCTS_OPTION,
  ALL_EMPLOYEE_GROUPS_PRODUCTS_OPTION,
  SuggestionSourceOption,
  SuggestionSourceSelector,
} from './SuggestionSourceSelector';

type BuyItAgainSuggestionBoxProps = {
  selectedEmployeeId: string;
  selectedProductTypes?: ProductTypeName[];
  needsWindowsZtpAvailable?: boolean;
  selectedCountryCode: Country;
  specificProductOriginName?: ProductOriginName;
  selectedItemsIds: string[];
  onAddItem: (item: Product) => void;
  onRemoveItem: (item: Product) => void;
};

export const BuyItAgainSuggestionBox = ({
  selectedEmployeeId,
  selectedProductTypes,
  needsWindowsZtpAvailable,
  selectedCountryCode,
  specificProductOriginName,
  selectedItemsIds,
  onAddItem,
  onRemoveItem,
}: BuyItAgainSuggestionBoxProps) => {
  const { t } = useTranslation('common');
  const [isCollapsed, setIsCollapsed] = useState(false);

  const { md } = useBreakpoint();
  const itemsPerPage = md ? 4 : 2;

  const [selectedSuggestionSource, setSelectedSuggestionSource] =
    useState<SuggestionSourceOption>();

  // item list (the ones to be displayed, with pagination, after filtering)
  const [itemList, setItemList] = useState<Product[]>([]);
  const [selectedPage, setSelectedPage] = useState(0);

  // go back to the first page when the number of items per page changes (e.g. resizing across the "md" breakpoint), so the page stays in range
  useEffect(() => {
    setSelectedPage(0);
  }, [itemsPerPage]);

  const organizationId = useGetOrganizationId();

  const {
    data: allSuggestions,
    isLoading,
    isError,
  } = useGetSuggestionsQuery({
    organizationId: organizationId,
    employeeId: selectedEmployeeId,
    productTypes: selectedProductTypes,
    windowsZtpAvailable: needsWindowsZtpAvailable,
    countryCode: selectedCountryCode,
    productOriginName: specificProductOriginName,
  });

  // updating the suggestion selector based on the initial suggestion response
  useEffect(() => {
    if (allSuggestions) {
      // auto-select the suggestion source based on the response
      if (
        allSuggestions.employeeGroups?.length > 1 &&
        allSuggestions.employeeGroups.some(group => group.products.length > 0)
      ) {
        setSelectedSuggestionSource(ALL_EMPLOYEE_GROUPS_PRODUCTS_OPTION);
      } else if (
        allSuggestions.employeeGroups?.length === 1 &&
        allSuggestions.employeeGroups[0].products.length > 0
      ) {
        setSelectedSuggestionSource({
          label: allSuggestions.employeeGroups[0].name,
          value: allSuggestions.employeeGroups[0].id,
          productsCount: allSuggestions.employeeGroups[0].products.length,
        });
      } else {
        setSelectedSuggestionSource(ALL_COMPANY_PRODUCTS_OPTION);
      }
    }
  }, [allSuggestions]);

  // updating the list based on the selected suggestion source
  useEffect(() => {
    if (selectedSuggestionSource) {
      if (
        selectedSuggestionSource.value === ALL_COMPANY_PRODUCTS_OPTION.value
      ) {
        setItemList(allSuggestions?.companyProducts || []);
      } else if (
        selectedSuggestionSource.value ===
        ALL_EMPLOYEE_GROUPS_PRODUCTS_OPTION.value
      ) {
        setItemList(allSuggestions?.allEmployeeGroupsProducts || []);
      } else {
        setItemList(
          allSuggestions?.employeeGroups.find(
            group => group.id === selectedSuggestionSource.value
          )?.products || []
        );
      }

      // go back to the first page
      setSelectedPage(0);
    }
  }, [allSuggestions, selectedSuggestionSource]);

  const getGroupNamesForItem = (
    selectedSuggestionSource: SuggestionSourceOption,
    item: Product
  ) => {
    if (selectedSuggestionSource.value === ALL_COMPANY_PRODUCTS_OPTION.value) {
      // when showing all company products, we don't show the group names, because we don't know all the company groups that have it associated
      return undefined;
    } else if (
      // when showing products for all employee groups, we show all the group names, because we can cross-reference them from "allSuggestions?.employeeGroups"
      selectedSuggestionSource.value ===
      ALL_EMPLOYEE_GROUPS_PRODUCTS_OPTION.value
    ) {
      return allSuggestions?.employeeGroups
        ?.filter(group =>
          group.products.some(product => product.id === item.id)
        )
        ?.map(group => group.name);
    } else {
      // wheen showing products for a specific group, we show just this group name
      return [selectedSuggestionSource.label];
    }
  };

  if (isError) {
    return (
      <ErrorState
        title={t('storefront.buyItAgain.errorTitle')}
        message={t('storefront.buyItAgain.errorMessage')}
      />
    );
  }

  if (
    isLoading ||
    !allSuggestions?.companyProducts ||
    allSuggestions?.companyProducts.length === 0
  ) {
    // when there is no company products, there is no suggestions at all to show, so we don't render the BuyItAgain box
    return null;
  }

  return (
    <Box width="100%">
      <CollapseBox
        title={t('storefront.buyItAgain.title')}
        subtitle={t('storefront.buyItAgain.subtitle')}
        isCollapsed={isCollapsed}
        onToggle={() => setIsCollapsed(state => !state)}>
        {/* Content */}
        <Flex vertical width="100%">
          <SuggestionSourceSelector
            availableOptions={[
              {
                ...ALL_COMPANY_PRODUCTS_OPTION,
                productsCount: allSuggestions.companyProducts.length,
              },
              {
                ...ALL_EMPLOYEE_GROUPS_PRODUCTS_OPTION,
                productsCount: allSuggestions.allEmployeeGroupsProducts.length,
              },
              ...allSuggestions.employeeGroups.map(group => ({
                label: group.name,
                value: group.id,
                productsCount: group.products.length,
              })),
            ]}
            selectedOption={
              selectedSuggestionSource ?? ALL_COMPANY_PRODUCTS_OPTION
            }
            onSelect={newSelectedOption =>
              setSelectedSuggestionSource(newSelectedOption)
            }
          />
          <Box
            marginTop={spaces.space_m}
            width="100%"
            display="grid"
            gridTemplateColumns={`repeat(${itemsPerPage}, 1fr)`}
            columnGap={spaces.space_m}>
            {itemList
              .slice(
                selectedPage * itemsPerPage, // start index
                selectedPage * itemsPerPage + itemsPerPage // end index
              )
              .map(item => {
                return (
                  <ItemCard
                    key={item.id}
                    idPrefix="buy-it-again"
                    store={Store.Catalog}
                    item={item}
                    selected={selectedItemsIds.includes(item.id)}
                    onAddItem={() => onAddItem(item)}
                    onRemoveItem={() => onRemoveItem(item)}
                    isCondensed
                    groupNames={
                      selectedSuggestionSource &&
                      getGroupNamesForItem(selectedSuggestionSource, item)
                    }
                  />
                );
              })}
          </Box>
          <Box marginTop={spaces.space_s} alignSelf="center">
            <TranslatedPagination
              id="buy-it-again-pagination"
              currentPage={selectedPage + 1} // using zero-based pagination in the array (but showing starting from 1 to the user)
              totalPageCount={Math.ceil(itemList.length / itemsPerPage)}
              pageChangeCallback={(_previousPageNumber, nextPageNumber) => {
                // The "pageChangeCallback" (Arc) pass 2 arguments:
                // 1- "previousPageNumber": The current page (we don't need it)
                // 2- "nextPageNumber": The new desired page (can be the next or previous page).
                setSelectedPage(nextPageNumber - 1); // converting back to zero-based pagination
              }}
            />
          </Box>
        </Flex>
      </CollapseBox>
    </Box>
  );
};
